import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { TERMS_OF_USE } from '../../constants';

export const TermsOfUseModal = ({ onAccept, onDecline, accepting = false }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="bg-levelup-dark p-6 text-white">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-levelup-accent" size={28} />
            <div>
              <h2 className="text-xl font-bold">Terms of Use Agreement</h2>
              <p className="text-gray-300 text-sm">Please read and accept before downloading content</p>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[50vh] bg-gray-50" onScroll={handleScroll}>
          <div className="prose prose-sm max-w-none">
            {TERMS_OF_USE.split('\n\n').map((paragraph, idx) => {
              if (paragraph.startsWith('LevelUp Partner')) {
                return <h3 key={idx} className="text-lg font-bold text-levelup-dark mb-4">{paragraph}</h3>;
              }
              if (paragraph.match(/^\d+\./)) {
                const [title, ...content] = paragraph.split('\n');
                return (
                  <div key={idx} className="mb-4">
                    <h4 className="font-semibold text-levelup-dark">{title}</h4>
                    {content.map((line, i) => (
                      <p key={i} className="text-gray-600 text-sm ml-4">
                        {line.startsWith('•') ? (
                          <span className="flex items-start">
                            <span className="text-levelup-accent mr-2">•</span>
                            {line.substring(1).trim()}
                          </span>
                        ) : line}
                      </p>
                    ))}
                  </div>
                );
              }
              return <p key={idx} className="text-gray-600 text-sm mb-3">{paragraph}</p>;
            })}
          </div>
          {!scrolledToBottom && (
            <div className="text-center text-gray-400 text-sm mt-4 animate-bounce">
              ↓ Scroll down to read all terms ↓
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-white">
          <label className="flex items-start space-x-3 mb-4">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
              disabled={!scrolledToBottom}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-levelup-accent focus:ring-levelup-accent disabled:opacity-50"
              data-testid="terms-checkbox" />
            <span className={`text-sm ${scrolledToBottom ? 'text-gray-700' : 'text-gray-400'}`}>
              I have read, understood, and agree to the Terms of Use. I confirm that I am authorized to act on behalf of my organization and will ensure compliance across my delivery teams.
            </span>
          </label>
          <div className="flex space-x-4">
            <button onClick={onDecline} disabled={accepting}
              className="flex-1 px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-50"
              data-testid="terms-decline-button">
              Decline
            </button>
            <button onClick={onAccept} disabled={!accepted || !scrolledToBottom || accepting}
              className="flex-1 bg-levelup-accent text-levelup-darker px-6 py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="terms-accept-button">
              {accepting ? 'Saving...' : 'Accept & Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
